import { useEffect, useRef, useState, useCallback, useMemo } from "react";
import birdSpriteUrl from "../../assets/img/bird.png";
import pipeTopSpriteUrl from "../../assets/img/pipe-top.png";
import pipeBottomSpriteUrl from "../../assets/img/pipe-bottom.png";
import bgSpriteUrl from "../../assets/img/background.jpg";

export default function FlappyBird() {
  const canvasRef = useRef(null);
  const animationIdRef = useRef(null);
  const [gameOver, setGameOver] = useState(false);
  const [score, setScore] = useState(0);
  const [highScore, setHighScore] = useState(0);
  const [imagesLoaded, setImagesLoaded] = useState(false);
  const [gameStarted, setGameStarted] = useState(false);
  const gameStartedRef = useRef(gameStarted);
    useEffect(() => {
    gameStartedRef.current = gameStarted;
    }, [gameStarted]);
  const gameOverRef = useRef(gameOver);
  useEffect(() => {
    gameOverRef.current = gameOver;
  }, [gameOver]);

  // Load high score from localStorage once on mount
  useEffect(() => {
    const savedHighScore = parseInt(localStorage.getItem("flappyBirdHighScore"), 10) || 0;
    setHighScore(savedHighScore);
  }, []);

  // Update high score if score surpasses it
  useEffect(() => {
    if (score > highScore) {
      setHighScore(score);
      localStorage.setItem("flappyBirdHighScore", score.toString());
    }
  }, [score, highScore]);

  const sprites = useMemo(() => {
    const birdSprite = new Image();
    birdSprite.src = birdSpriteUrl;

    const pipeTopSprite = new Image();
    pipeTopSprite.src = pipeTopSpriteUrl;

    const pipeBottomSprite = new Image();
    pipeBottomSprite.src = pipeBottomSpriteUrl;

    const bgSprite = new Image();
    bgSprite.src = bgSpriteUrl;

    return { birdSprite, pipeTopSprite, pipeBottomSprite, bgSprite };
  }, []);

  const birdRef = useRef({ x: 50, y: 150, width: 34, height: 24, velocity: 0 });
  const gravity = 0.12;
  const jump = -3.5;
  const pipesRef = useRef([]);
  const pipeGap = 120;
  const pipeWidth = 52;
  const pipeSpeed = 2;
  const frameCountRef = useRef(0);

  const endGame = useCallback(() => {
    cancelAnimationFrame(animationIdRef.current);
    setGameOver(true);
    setGameStarted(false);
  }, []);

  const gameSpeedDivider = 3; // update game logic every 3 frames to slow speed

const loop = useCallback(() => {
  const { birdSprite, pipeTopSprite, pipeBottomSprite, bgSprite } = sprites;
  const canvas = canvasRef.current;
  const ctx = canvas.getContext("2d");

  ctx.clearRect(0, 0, canvas.width, canvas.height);
  ctx.drawImage(bgSprite, 0, 0, canvas.width, canvas.height);

  const bird = birdRef.current;
  const pipes = pipesRef.current;

  // Render bird regardless
  ctx.drawImage(birdSprite, bird.x, bird.y, bird.width, bird.height);

  if (!gameOverRef.current && gameStartedRef.current) {
    if (frameCountRef.current % gameSpeedDivider === 0) {
      // Bird physics (updated less often)
      bird.velocity += gravity;
      bird.y += bird.velocity;

      // Pipe generation (also slower)
      if (frameCountRef.current % (90 * gameSpeedDivider) === 0) {
        let pipeTopHeight = Math.random() * (canvas.height - pipeGap - 60) + 20;
        pipes.push({ x: canvas.width, topHeight: pipeTopHeight });
      }

      // Move pipes (less often)
      for (let i = pipes.length - 1; i >= 0; i--) {
        let p = pipes[i];
        p.x -= pipeSpeed;

        // Collision detection
        if (
          bird.x < p.x + pipeWidth &&
          bird.x + bird.width > p.x &&
          (bird.y < p.topHeight || bird.y + bird.height > p.topHeight + pipeGap)
        ) {
          endGame();
          return;
        }

        // Remove off screen pipes
        if (p.x + pipeWidth < 0) pipes.splice(i, 1);

        // Score pipes
        if (p.x + pipeWidth < bird.x && !p.scored) {
          setScore((prev) => prev + 1);
          p.scored = true;
        }
      }

      // Ground / Ceiling collision
      if (bird.y + bird.height >= canvas.height || bird.y <= 0) {
        endGame();
        return;
      }
    }

    frameCountRef.current++;
  }

  // Draw pipes every frame (for smoothness)
  for (let p of pipes) {
    ctx.drawImage(pipeBottomSprite, p.x, 0, pipeWidth, p.topHeight);
    ctx.drawImage(
      pipeTopSprite,
      p.x,
      p.topHeight + pipeGap,
      pipeWidth,
      canvas.height - p.topHeight - pipeGap
    );
  }

  animationIdRef.current = requestAnimationFrame(loop);
}, [endGame, sprites]);

  const resetGame = useCallback(() => {
    birdRef.current = { x: 50, y: 150, width: 34, height: 24, velocity: 0 };
    pipesRef.current = [];
    frameCountRef.current = 0;
    setScore(0);
    setGameOver(false);
    setGameStarted(false);
    animationIdRef.current = requestAnimationFrame(loop);
  }, [loop]);

  const handleJump = useCallback(() => {
    if (!gameStartedRef.current && !gameOverRef.current) {
      setGameStarted(true);
      animationIdRef.current = requestAnimationFrame(loop);
    }
    birdRef.current.velocity = jump;
  }, [jump, loop]);

  useEffect(() => {
    const canvas = canvasRef.current;
    canvas.width = 400;
    canvas.height = 512;

    const keyHandler = (e) => {
      if (e.code === "Space") handleJump();
    };
    window.addEventListener("keydown", keyHandler);
    canvas.addEventListener("click", handleJump);

    // Wait for all images to load
    const imgs = Object.values(sprites);
    let loaded = 0;
    imgs.forEach((img) => {
      img.onload = () => {
        loaded++;
        if (loaded === imgs.length) {
          setImagesLoaded(true);
          animationIdRef.current = requestAnimationFrame(loop);
        }
      };
    });

    return () => {
      cancelAnimationFrame(animationIdRef.current);
      window.removeEventListener("keydown", keyHandler);
      canvas.removeEventListener("click", handleJump);
    };
  }, [handleJump, loop, sprites]);

  return (
    <div style={{ textAlign: "center" }}>
      <canvas
        ref={canvasRef}
        style={{
          background: "#70c5ce",
          display: "block",
          margin: "0 auto",
          borderRadius: "8px",
        }}
      />
      <div style={{ fontSize: "18px", margin: "10px 0" }}>Score: {score} | High Score: {highScore}</div>
      {!gameStarted && imagesLoaded && !gameOver && (
        <div style={{ fontStyle: "italic", marginBottom: "10px" }}>
          Press Space or Click to Start
        </div>
      )}
      {gameOver && (
        <button
          onClick={resetGame}
          style={{
            padding: "8px 16px",
            fontSize: "16px",
            background: "#ff9800",
            color: "#fff",
            border: "none",
            borderRadius: "5px",
            cursor: "pointer",
          }}
        >
          Restart
        </button>
      )}
      {!imagesLoaded && <div>Loading sprites...</div>}
    </div>
  );
}
